import React, { useState, useEffect } from 'react';
import { getUserContributions } from '../services/firestoreService';

const Contributions = ({ user }) => {
    const [contributions, setContributions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('all'); // 'all', 'completed', 'pending'

    useEffect(() => {
        loadContributions();
    }, [user]);

    const loadContributions = async () => {
        try {
            setLoading(true);
            setError('');
            const contributionsData = await getUserContributions(user.uid);
            setContributions(contributionsData);
        } catch (error) {
            console.error('Error loading contributions:', error);
            setError('Failed to load your contributions. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return 'Invalid Date';
        }
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            completed: { color: 'green', text: 'Completed', icon: '✅' },
            pending: { color: 'yellow', text: 'Pending', icon: '⏳' },
            failed: { color: 'red', text: 'Failed', icon: '❌' },
            processing: { color: 'blue', text: 'Processing', icon: '🔄' }
        };

        const config = statusConfig[status] || statusConfig.completed;
        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
        <span className="mr-1">{config.icon}</span>
                {config.text}
      </span>
        );
    };

    const getPaymentMethodIcon = (method) => {
        const icons = {
            voucher: '🎫',
            wallet: '💰',
            card: '💳',
            bank: '🏦'
        };
        return icons[method] || '💸';
    };

    const filteredContributions = contributions.filter(contribution => {
        if (filter === 'all') return true;
        return contribution.status === filter;
    });

    const stats = {
        totalContributions: contributions.length,
        totalAmount: contributions.reduce((sum, contribution) => sum + (contribution.amount || 0), 0),
        completedContributions: contributions.filter(c => c.status === 'completed').length,
        pendingContributions: contributions.filter(c => c.status === 'pending').length
    };

    if (loading) {
        return (
            <div className="space-y-6">
                {/* Loading Skeleton for Summary */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="animate-pulse">
                        <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {[1, 2, 3].map(i => (
                                <div key={i} className="text-center p-4 bg-gray-50 rounded-lg">
                                    <div className="h-8 bg-gray-200 rounded w-1/2 mx-auto mb-2"></div>
                                    <div className="h-4 bg-gray-200 rounded w-1/3 mx-auto"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Loading Skeleton for Contributions */}
                <div className="bg-white rounded-lg shadow-sm">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                    </div>
                    <div className="p-6">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="animate-pulse flex items-center justify-between p-4 border border-gray-200 rounded-lg mb-4">
                                <div className="flex items-center space-x-4">
                                    <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                                    <div>
                                        <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                                        <div className="h-3 bg-gray-200 rounded w-24"></div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="h-4 bg-gray-200 rounded w-16 mb-2"></div>
                                    <div className="h-3 bg-gray-200 rounded w-20"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="text-center py-8">
                        <div className="text-red-500 text-4xl mb-4">⚠️</div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Contributions</h3>
                        <p className="text-gray-600 mb-4">{error}</p>
                        <button
                            onClick={loadContributions}
                            className="bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Summary Card */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Contribution Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-primary-50 rounded-lg">
                        <p className="text-2xl font-bold text-primary-600">{stats.totalContributions}</p>
                        <p className="text-sm text-gray-600">Total Contributions</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                        <p className="text-2xl font-bold text-green-600">{formatAmount(stats.totalAmount)}</p>
                        <p className="text-sm text-gray-600">Total Amount Given</p>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <p className="text-2xl font-bold text-blue-600">{stats.completedContributions}</p>
                        <p className="text-sm text-gray-600">Completed</p>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                        <p className="text-2xl font-bold text-yellow-600">{stats.pendingContributions}</p>
                        <p className="text-sm text-gray-600">Pending</p>
                    </div>
                </div>
            </div>

            {/* Contributions History */}
            <div className="bg-white rounded-lg shadow-sm">
                <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-900">Contribution History</h3>

                        {/* Filter Buttons */}
                        <div className="flex space-x-2">
                            <button
                                onClick={() => setFilter('all')}
                                className={`px-3 py-1 text-sm font-medium rounded-lg transition-colors ${
                                    filter === 'all'
                                        ? 'bg-primary-500 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                All ({contributions.length})
                            </button>
                            <button
                                onClick={() => setFilter('completed')}
                                className={`px-3 py-1 text-sm font-medium rounded-lg transition-colors ${
                                    filter === 'completed'
                                        ? 'bg-green-500 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                Completed ({stats.completedContributions})
                            </button>
                            <button
                                onClick={() => setFilter('pending')}
                                className={`px-3 py-1 text-sm font-medium rounded-lg transition-colors ${
                                    filter === 'pending'
                                        ? 'bg-yellow-500 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                Pending ({stats.pendingContributions})
                            </button>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    {filteredContributions.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="text-gray-400 text-6xl mb-4">
                                {filter === 'all' ? '💝' : filter === 'completed' ? '✅' : '⏳'}
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                {filter === 'all'
                                    ? 'No Contributions Yet'
                                    : filter === 'completed'
                                        ? 'No Completed Contributions'
                                        : 'No Pending Contributions'
                                }
                            </h3>
                            <p className="text-gray-600">
                                {filter === 'all'
                                    ? "You haven't made any contributions yet. Start supporting community members in need."
                                    : filter === 'completed'
                                        ? "You don't have any completed contributions yet."
                                        : "You don't have any pending contributions."
                                }
                            </p>
                            {filter !== 'all' && (
                                <button
                                    onClick={() => setFilter('all')}
                                    className="mt-4 bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition-colors"
                                >
                                    View All Contributions
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {filteredContributions.map((contribution) => (
                                <div key={contribution.id} className="flex items-center justify-between p-6 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                                    <div className="flex items-center space-x-4 flex-1">
                                        {/* Contribution Icon */}
                                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl ${
                                            contribution.status === 'completed' ? 'bg-green-100 text-green-600' :
                                                contribution.status === 'pending' ? 'bg-yellow-100 text-yellow-600' :
                                                    contribution.status === 'failed' ? 'bg-red-100 text-red-600' :
                                                        'bg-blue-100 text-blue-600'
                                        }`}>
                                            {getPaymentMethodIcon(contribution.paymentMethod)}
                                        </div>

                                        {/* Contribution Details */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center space-x-2 mb-2">
                                                <h4 className="font-semibold text-gray-900 truncate">
                                                    {contribution.caseMemberName ? `Support for ${contribution.caseMemberName}` : 'Community Support'}
                                                </h4>
                                                {getStatusBadge(contribution.status)}
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-600">
                                                <div className="flex items-center space-x-1">
                                                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                    <span>{formatDate(contribution.createdAt)}</span>
                                                </div>

                                                {contribution.relationship && (
                                                    <div className="flex items-center space-x-1">
                                                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                        </svg>
                                                        <span>Loss of {contribution.relationship}</span>
                                                    </div>
                                                )}

                                                <div className="flex items-center space-x-1">
                                                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                                    </svg>
                                                    <span className="capitalize">
                            {contribution.paymentMethod === 'voucher' ? 'Voucher Payment' :
                                contribution.paymentMethod === 'wallet' ? 'Wallet Balance' :
                                    contribution.paymentMethod || 'Payment'}
                          </span>
                                                </div>

                                                {contribution.voucherNumber && (
                                                    <div className="flex items-center space-x-1">
                                                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                                                        </svg>
                                                        <span className="font-mono text-xs">{contribution.voucherNumber}</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Transaction ID */}
                                            {contribution.id && (
                                                <div className="mt-2 flex items-center space-x-1 text-xs text-gray-500">
                                                    <span>Transaction:</span>
                                                    <span className="font-mono">{contribution.id.slice(0, 8)}...</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Amount and Actions */}
                                    <div className="text-right ml-4">
                                        <p className="text-2xl font-bold text-primary-600">
                                            {formatAmount(contribution.amount || 0)}
                                        </p>
                                        <div className="mt-2 flex space-x-2">
                                            <button className="text-primary-600 hover:text-primary-800 text-sm font-medium">
                                                Receipt
                                            </button>
                                            {contribution.caseId && (
                                                <button className="text-gray-600 hover:text-gray-800 text-sm font-medium">
                                                    View Case
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Export/Refresh Options */}
                    {filteredContributions.length > 0 && (
                        <div className="mt-6 flex justify-between items-center">
                            <button
                                onClick={loadContributions}
                                className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
                            >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                <span>Refresh</span>
                            </button>

                            <div className="flex space-x-2">
                                <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium">
                                    Export CSV
                                </button>
                                <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium">
                                    Print Summary
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Contributions;